import React from "react";
import { AgGridReact, Button, Flex, Grid, InfoBox, Link, Text } from "@jsq/ds/core";
import { NameRenderer } from "../common/NameRenderer";
import { OwnerOrControllerSummaryListActions } from "../common/OwnerOrControllerSummaryListActions";
import { RowItem, TypedAGGridCellRenderParams } from "../common/types";
import { infoBoxStyle, titleStyle } from "./style_vanilla.css";
import { ControllerIndividualSelectionProps } from "./types";

export const ControllerIndividualSelection: React.FC<ControllerIndividualSelectionProps> = ({
  entityName,
  controllers,
  onAddController,
  onCompleted,
  onBack,
  onEdit,
  onDelete,
}: ControllerIndividualSelectionProps) => {
  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    suppressCellFocus: true,
    domLayout: "autoHeight",
    rowHeight: 61,
  };
  const colDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
    {
      field: "name",
      headerName: "Name",
      cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) => (
        <NameRenderer params={params} />
      ),
    },
    {
      field: "relationship",
      headerName: "Relationship",
    },
    {
      maxWidth: 100,
      resizable: false,
      suppressMenu: true,
      suppressMovable: true,
      sortable: false,
      field: "actions",
      headerName: "",
      cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) => (
        <OwnerOrControllerSummaryListActions
          params={params}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ),
    },
  ];

  return (
    <Flex flexDirection="column">
      {entityName && (
        <Link
          to=""
          data-testid="controller-individual-selection-back-link"
          variant="muted"
          icon="arrow-left"
          iconPosition="left"
          align="left"
          onClick={onBack}
        >
          {entityName}
        </Link>
      )}
      <Text variant="h3" className={titleStyle}>
        {`Controllers of ${entityName}`}
      </Text>
      <InfoBox variant="info" title="Controller" className={infoBoxStyle}>
        A controller is an individual with significant responsibility for managing the business.
        Examples include Chief Executive Officer, Chief Financial Officer, Chief Operating
        Officer, Managing Member, General Partner, President, Vice President or Treasurer.
      </InfoBox>

      <Flex flexDirection="column" gap={20}>
        <Flex>
          <Button
            icon="plus"
            onClick={() => {
              onAddController();
            }}
          >
            Add controller
          </Button>
        </Flex>
        <Grid gridOptions={gridOptions} rowData={controllers} columnDefs={colDefs}></Grid>
        <Flex justifyContent="flex-end" gap={8}>
          <Button
            variant="neutral"
            onClick={() => {
              onBack();
            }}
          >
            Back to owners
          </Button>
          <Button onClick={() => onCompleted()}>Complete</Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
